import {
  Card,
  CardActionArea,
  CardContent,
  CardMedia,
  Chip,
  Stack,
  SxProps,
  Theme,
  Typography,
} from '@mui/material'
import { useMemo } from 'react'
import { Icons } from 'utoniq-ui'

import {
  ArtistTopEventFragment,
  Event,
  EventListFragment,
  EventPreSaleConnectionFragment,
  GlobalEventFragment,
  Maybe,
  PreSaleType,
} from '@/clients/utoniq-core/schema'
import { styleSets } from '@/components/styles'
import { config } from '@/config'
import { useRouter, useTranslation } from '@/core'
import { Organism } from '@/core/architecture'
import { DateTime } from '@/core/entities'
import { useAnalytics } from '@/lib/analytics'

export type EventCardProps = {
  event: EventListFragment | GlobalEventFragment | ArtistTopEventFragment
  sx?: SxProps<Theme>
}

export const EventCard: Organism<EventCardProps> = ({ event, sx }) => {
  const { generatePath, routerPages } = useRouter()
  const { analytics } = useAnalytics()

  return (
    <Card sx={{ borderRadius: 2, ...sx }}>
      <CardActionArea
        href={generatePath(routerPages.ShokufesEventDetail, {
          eventId: event.id,
        })}
        sx={{ height: '100%' }}
        onClick={() => {
          analytics?.track('click_select', {
            applicationKey: config.applicationKey,
            eventId: event.id,
            name: 'eventFes',
          })
        }}
      >
        <CardMedia
          sx={{ height: 160 }}
          image={event.thumbnail?.mediumUri ?? ''}
          title={event.title}
        />
        <CardContent sx={{ p: 4, '&:last-child': { pb: 4 } }}>
          {!!(event.startedAt || event.endedAt) && (
            <EventCategory event={event} />
          )}
          <Typography variant="h6" sx={{ pt: 2, ...styleSets.lineCamp(2) }}>
            {event.title}
          </Typography>
          {event.dateRange && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ pt: 2 }}
            >
              <Icons.Calendar fontSize="small" sx={{ color: 'grey.700' }} />
              <Typography variant="overline" color="text.secondary">
                {event.dateRange}
              </Typography>
            </Stack>
          )}
          {event.location && (
            <Stack
              direction="row"
              spacing={1}
              alignItems="center"
              sx={{ pt: 1 }}
            >
              <Icons.Pin fontSize="small" sx={{ color: 'grey.700' }} />
              <Typography variant="overline" color="text.secondary">
                {event.location?.approximateAddress}
              </Typography>
            </Stack>
          )}
        </CardContent>
      </CardActionArea>
    </Card>
  )
}

type EventCategoryProps = {
  event: Pick<Event, 'startedAt' | 'endedAt'> & {
    preSales: Maybe<EventPreSaleConnectionFragment>
  }
}
export const EventCategory: Organism<EventCategoryProps> = ({ event }) => {
  const { t } = useTranslation('events')

  const { color, bgcolor, text } = useMemo(() => {
    const havePreSales = !!event.preSales?.edges?.length

    if (havePreSales) {
      //先行販売(先着・抽選)が期間前 → 受付前
      const upcomingPreSale = event.preSales?.edges.every(({ node }) => {
        if (node.type === PreSaleType.Lottery) {
          return DateTime.isGt(node.lottery?.applicationStartedAt)
        }
        return DateTime.isGt(node.firstComeFirstServed?.purchasableStartedAt)
      })
      if (upcomingPreSale) {
        return {
          color: 'text.primary',
          bgcolor: 'info.light',
          text: t('upcomingPreSale', '受付前'),
        }
      }

      //先行販売(先着・抽選)が期間内 → 受付中
      const ongoingPreSale = event.preSales?.edges?.some(({ node }) => {
        if (node.type === PreSaleType.Lottery) {
          return DateTime.isBetween(
            node.lottery?.applicationStartedAt,
            node.lottery?.applicationEndedAt
          )
        }
        return DateTime.isBetween(
          node.firstComeFirstServed?.purchasableStartedAt,
          node.firstComeFirstServed?.purchasableEndedAt
        )
      })
      if (ongoingPreSale) {
        return {
          color: 'text.primary',
          bgcolor: 'info.light',
          text: t('ongoingPreSale', '受付中'),
        }
      }
    }
    //先行販売期間後

    // event not set endedAt & startedAt → 開催日設定なし
    if (!event.endedAt && !event.startedAt) {
      return {
        color: 'text.secondary',
        bgcolor: 'grey.100',
        text: t('noDateSet', '開催日設定なし'),
      }
    }

    //イベント期間が過去 → 終了
    if (event.endedAt && DateTime.isLt(event.endedAt)) {
      return {
        color: 'text.disabled',
        bgcolor: 'grey.100',
        text: t('end', '終了'),
      }
    }

    //イベント期間が未来 → 開催前
    if (DateTime.isGt(event.startedAt)) {
      return {
        color: 'text.primary',
        bgcolor: 'info.light',
        text: t('upcoming', '開催前'),
      }
    }

    //イベント中 → 開催中
    return {
      color: 'white',
      bgcolor: 'secondary.main',
      text: t('ongoing', '開催中'),
    }
  }, [event.endedAt, event.preSales?.edges, event.startedAt, t])

  return (
    <Chip
      size="small"
      sx={{
        height: 20,
        borderRadius: '2px',
        minWidth: 40,
        bgcolor,
        '.MuiChip-label': {
          px: 1,
        },
      }}
      label={
        <Typography color={color} variant="overline" display="block">
          {text}
        </Typography>
      }
    />
  )
}
